// CU-86d45b38j — client for finance's subject-bound customer billing. Ownership is the signed token on the server, so no
// call carries a user id or a Stripe customer id; the only browser-supplied value is the Stripe.js payment method id.
import { ENV } from "../../config/runtimeEnv";
import type { ApiResponse } from "../../types/finance/FinanceTypes";
import { http } from "../../lib/http/client";

const FINANCE_BASE_URL = ENV.VITE_FINANCE_SERVER;

export interface CustomerBillingAccount {
	email: string | null;
	name: string | null;
	phone: string | null;
}

export interface CustomerBillingOverview {
	payments: {
		total: number;
		completed: number;
		pending: number;
		failed: number;
		totalAmount: number;
	};
	invoices: {
		total: number;
		paid: number;
		pending: number;
		overdue: number;
		totalAmount: number;
	};
}

export interface CustomerBillingState {
	available: boolean;
	setupRequired: boolean;
	account: CustomerBillingAccount | null;
	overview: CustomerBillingOverview | null;
}

export interface CustomerPaymentMethod {
	id: string;
	type: string;
	card: {
		brand: string;
		last4: string;
		exp_month: number;
		exp_year: number;
		funding: string;
	} | null;
	billing_details: {
		name: string | null;
	};
	created: number;
	isDefault: boolean;
}

const billingUrl = (suffix = ""): string => `${FINANCE_BASE_URL}/customer/billing${suffix}`;

export const fetchCustomerBilling = async (): Promise<CustomerBillingState> => {
	const response = await http.get<ApiResponse<CustomerBillingState>>(billingUrl());
	return response.data.data;
};

export const setupCustomerBilling = async (): Promise<CustomerBillingState> => {
	const response = await http.post<ApiResponse<CustomerBillingState>>(billingUrl("/setup"), {});
	return response.data.data;
};

export const fetchCustomerPaymentMethods = async (): Promise<CustomerPaymentMethod[]> => {
	const response = await http.get<ApiResponse<CustomerPaymentMethod[]>>(billingUrl("/payment-methods"));
	return response.data.data;
};

export const attachCustomerPaymentMethod = async (paymentMethodId: string): Promise<CustomerPaymentMethod> => {
	const response = await http.post<ApiResponse<CustomerPaymentMethod>>(
		billingUrl("/payment-methods"),
		{ paymentMethodId },
	);
	return response.data.data;
};

export const setDefaultCustomerPaymentMethod = async (paymentMethodId: string): Promise<void> => {
	await http.post(billingUrl(`/payment-methods/${encodeURIComponent(paymentMethodId)}/default`), {});
};

export const detachCustomerPaymentMethod = async (paymentMethodId: string): Promise<void> => {
	await http.delete(billingUrl(`/payment-methods/${encodeURIComponent(paymentMethodId)}`));
};
